import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { createInitialValue, ProjectFormModel } from '../model'
import { useEditedFormData } from '../../../containers/SingleSheetV2/hooks/editedFormData'
import { usePageDataIsEdited } from '../../../hooks/usePageDataIsEdited'
import { OnChangeProject } from './projectModelModifier'
import { popFunctionLayer } from '../../../../store/functionLayer'

// projectEdit をダイアログで開く際の設定。DBには保存せず、登録時に onComplete で
// 入力値を呼び出し元へ返す（呼び出し元がローカルに保持・後でまとめて保存する想定）。
export type ProjectEditDialogConfig = {
  // 初期値。既存プロジェクト編集 or 追加直後の下書き再編集で渡す。未指定なら新規。
  initial?: ProjectFormModel
  // 新規プロジェクト（コード・採番形式が編集可）かどうか。
  isNew: boolean
  // 登録押下時に呼ぶ。入力値（form model）を呼び出し元へ返す。
  onComplete: (formModel: ProjectFormModel) => void
}

// ダイアログ版の data フック。DB保存・遷移・削除は行わない。
// 編集フラグはグローバルの requiredSaveData を使う。FunctionLayer が開閉時に親画面の
// 状態を退避/復元するため、LayerDialog の確認(confirmClose)・離脱ガードがこの dirty
// 状態を見て、× / ESC / 背景クリックなど全ての閉じ方で確認ダイアログを出せる。
export const useProjectEditDialogData = (dialog: ProjectEditDialogConfig) => {
  const dispatch = useDispatch()
  const [formModel, setFormModel] = useState<ProjectFormModel>(
    dialog.initial ?? createInitialValue()
  )
  // submit から最新の formModel を deps なしで参照するための ref。
  const formModelRef = useRef(formModel)
  useEffect(() => {
    formModelRef.current = formModel
  }, [formModel])
  const { edited, editedPageData, refreshedPageData } = usePageDataIsEdited()
  const { editedFormData, editFormData, refreshEditedFormData } =
    useEditedFormData()

  const onChangeProject = useCallback<OnChangeProject>(
    (path, value) => {
      setFormModel(prev => ({ ...prev, [path]: value }))
      editedPageData()
      editFormData(path)
    },
    [editedPageData, editFormData]
  )

  const submit = useCallback(async () => {
    dialog.onComplete(formModelRef.current)
    dispatch(popFunctionLayer())
  }, [dialog, dispatch])

  const reload = useCallback(() => {
    setFormModel(dialog.initial ?? createInitialValue())
    refreshedPageData()
    refreshEditedFormData()
  }, [dialog, refreshedPageData, refreshEditedFormData])

  return {
    isCreating: dialog.isNew,
    initialized: true,
    isLoading: false,
    edited,
    editedFormData,
    formModel,
    onChangeProject,
    submit,
    reload,
  }
}
